import { addDays, formatRate, isoDate, type QualityDashboard } from '@clim/domain';
import { useEffect, useState } from 'react';
import { Empty, ErrorBox, Field, Input, Select } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

/**
 * Tableau de bord qualité (§12) : poses par technicien, nombre de SAV, taux de SAV, détail par
 * motif et par cause ; filtres période / territoire / motif. Lecture seule : l'historique n'est jamais modifié.
 */
export function QualityPage() {
  const engine = useEngine();
  const { api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const today = isoDate(new Date());
  const [from, setFrom] = useState(addDays(today, -89));
  const [to, setTo] = useState(today);
  const [reasonId, setReasonId] = useState('');
  const [data, setData] = useState<QualityDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reasons = engine.list('reason').filter((r) => r.category === 'sav_reason');
  useEffect(() => {
    if (!online) return;
    const q = new URLSearchParams({ from, to, ...(territoryId ? { territoryId } : {}), ...(reasonId ? { reasonId } : {}) });
    api.request<QualityDashboard>('GET', `/v1/quality?${q}`).then(
      (d) => (setData(d), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, from, to, online, reasonId, territoryId]);
  const max = (list: { count: number }[]) => Math.max(1, ...list.map((x) => x.count));
  return (
    <div>
      <h1>Qualité</h1>
      <div className="filters">
        <Field label="Du">
          <Input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </Field>
        <Field label="Au">
          <Input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </Field>
        <Field label="Motif de SAV">
          <Select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
            <option value="">Tous les motifs</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {!online && <Empty>Le tableau de bord nécessite une connexion.</Empty>}
      <ErrorBox message={error} />
      {data && (
        <>
          <dl className="money" aria-label="Totaux qualité">
            <div>
              <dt>Poses / entretiens</dt>
              <dd>{data.totals.installs}</dd>
            </div>
            <div>
              <dt>SAV</dt>
              <dd>{data.totals.savs}</dd>
            </div>
            <div className="balance">
              <dt>Taux de SAV</dt>
              <dd>{formatRate(data.totals.rate)}</dd>
            </div>
          </dl>
          <div className="card">
            <h2>Par technicien</h2>
            {!data.technicians.length ? (
              <Empty>Aucune donnée sur la période.</Empty>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Technicien</th>
                    <th className="num">Poses</th>
                    <th className="num">SAV</th>
                    <th className="num">Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {data.technicians.map((t) => (
                    <tr key={t.technicianId}>
                      <td>{t.technicianName}</td>
                      <td className="num">{t.installs}</td>
                      <td className="num">{t.savs}</td>
                      <td className={`num${t.rate !== null && t.rate >= 0.1 ? ' negative' : ''}`}>{formatRate(t.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="grid2">
            <div className="card">
              <h2>Motifs de SAV</h2>
              {data.byReason.map((r) => (
                <div key={r.reasonId} className="bar-row">
                  <span>{r.label}</span>
                  <span className="bar" style={{ width: `${(r.count / max(data.byReason)) * 100}%` }} />
                  <strong>{r.count}</strong>
                </div>
              ))}
              {!data.byReason.length && <Empty>Aucun SAV.</Empty>}
            </div>
            <div className="card">
              <h2>Causes (SAV clôturés)</h2>
              {data.byCause.map((r) => (
                <div key={r.causeId ?? 'open'} className="bar-row">
                  <span>{r.label}</span>
                  <span className={`bar${r.causeId ? '' : ' muted-bar'}`} style={{ width: `${(r.count / max(data.byCause)) * 100}%` }} />
                  <strong>{r.count}</strong>
                </div>
              ))}
              {!data.byCause.length && <Empty>Aucun SAV.</Empty>}
            </div>
          </div>
          <p className="muted small">Un SAV est rattaché au technicien qui avait réalisé l’installation d’origine ; les visites SAV ne comptent pas comme des poses.</p>
        </>
      )}
    </div>
  );
}
